import {
  InvestmentsDashboardResponse,
  InvestmentDetailResponse,
  InvestmentSeriesPoint,
  InvestmentsListFamily,
  InvestmentsListRow,
} from "../api";

export type SeriesMode = "annual" | "quarterly";

export type ETFPricePoint = {
  date: string;
  close: number;
  volume: number | null;
};

export function coerceInvestmentsListData(response?: InvestmentsDashboardResponse): {
  rows: InvestmentsListRow[];
  tickers: string[];
  families: InvestmentsListFamily[];
  generatedAt: string;
} {
  const equityFamilies = response?.equityFamilies ?? [];
  const etfFamilies = response?.etfFamilies ?? [];
  const rows: InvestmentsListRow[] = [
    ...(response?.equityRows ?? []).map((row) => ({ ...row, instrumentType: "EQUITY" as const })),
    ...(response?.etfRows ?? []).map((row) => ({ ...row, instrumentType: "ETF" as const })),
  ];
  const tickerSet = new Set<string>();
  for (const family of [...equityFamilies, ...etfFamilies]) {
    for (const ticker of family.tickers ?? []) {
      tickerSet.add(ticker);
    }
  }
  for (const row of rows) {
    tickerSet.add(row.ticker);
  }
  return {
    rows,
    tickers: Array.from(tickerSet).sort(),
    families: [...equityFamilies, ...etfFamilies],
    generatedAt: response?.generatedAt ?? "",
  };
}

export function buildSortedSeries(
  data: InvestmentDetailResponse | null | undefined,
  mode: SeriesMode,
): InvestmentSeriesPoint[] {
  if (!data) {
    return [];
  }
  const annualSeries = Array.isArray(data.series?.annual) ? data.series.annual : [];
  const quarterlySeries = Array.isArray(data.series?.quarterly) ? data.series.quarterly : [];
  const source = mode === "annual" ? annualSeries : quarterlySeries;
  return [...source].sort((a, b) => a.periodEnd.localeCompare(b.periodEnd));
}

export function buildETFPriceSeries(data: InvestmentDetailResponse | null | undefined): ETFPricePoint[] {
  const historical = data?.etf?.historical;
  if (!historical) {
    return [];
  }
  return [...historical]
    .filter((point) => point.date && point.close !== null && point.close !== undefined)
    .map((point) => ({
      date: point.date,
      close: point.close as number,
      volume: point.volume ?? null,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function buildQueryErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  return fallback;
}

export * from "./dashboard";
